<script setup lang="ts">
const SIZES = { checkbox: { s: 13 }, radio: { s: 14 } }

const props = withDefaults(
  defineProps<{
    type: 'checkbox' | 'radio'
    checked: boolean
    disabled?: boolean
    name?: string
    size?: 's'
  }>(),
  { disabled: false, size: 's', name: undefined },
)

// destructuring here is fine: we do not need reactive size
const {
  value: {
    sizePx,
    indicatorWidthPx,
    indicatorHeightPx,
    checkmarkLeftPx,
    checkmarkTopPx,
  },
} = computed(() => {
  const size = SIZES[props.type][props.size]
  const radioSizeMultiplier = props.type === 'radio' ? 0.4 : undefined

  const indicatorHeight = Math.round(size * (radioSizeMultiplier || 0.7))
  const indicatorWidth = Math.round(size * (radioSizeMultiplier || 0.35))
  return {
    sizePx: `${size}px`,
    indicatorWidthPx: `${indicatorWidth}px`,
    indicatorHeightPx: `${indicatorHeight}px`,
    checkmarkTopPx: `${Math.round(0.06 * indicatorHeight)}px`,
    checkmarkLeftPx: `${Math.round(0.45 * indicatorHeight)}px`,
  }
})

const emit = defineEmits<{
  change: [e: UiChangeEvent<HTMLInputElement>]
}>()

const emitChange = (e: UiChangeEvent<HTMLInputElement>): void => {
  emit('change', e)
}
</script>

<template>
  <label class="ui-input">
    <input
      :type="type"
      :class="['ui-input__input', type === 'radio' ? '--radio' : '--checkbox']"
      :name="name"
      :checked="checked"
      :disabled="disabled"
      @change="emitChange($event as UiChangeEvent<HTMLInputElement>)"
    >
    <slot />
  </label>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/helpers' as *;

.ui-input {
  user-select: none;
  display: flex;
  align-items: center;

  @include min-width('lg') {
    &:hover {
      cursor: pointer;
      color: var(--app-color-link-hover);

      .ui-input__input::before {
        border-color: var(--app-color-link-hover);
      }

      .ui-input__input.--radio {
        opacity: 1;
        border-color: var(--app-color-link-hover);
      }
    }
  }
}

.ui-input__input {
  @extend %flex-row-center;

  /* stylelint-disable-next-line value-keyword-case */
  width: v-bind(sizePx);
  /* stylelint-disable-next-line value-keyword-case */
  height: v-bind(sizePx);
  position: relative;
  cursor: pointer;

  &.--checkbox::after {
    content: '';
    user-select: none;
    display: block;
    position: absolute;
    /* stylelint-disable-next-line value-keyword-case */
    top: v-bind(checkmarkTopPx);
    /* stylelint-disable-next-line value-keyword-case */
    left: v-bind(checkmarkLeftPx);
    /* stylelint-disable-next-line value-keyword-case */
    width: v-bind(indicatorWidthPx);
    /* stylelint-disable-next-line value-keyword-case */
    height: v-bind(indicatorHeightPx);
    border: solid transparent;
    border-width: 0 2px 2px 0;
    transform: rotate(39deg);
  }

  &.--checkbox::before {
    @extend %stretch-absolute;

    user-select: none;
    content: '';
    border: 1px solid var(--app-text-color-primary);
    border-radius: 2px;
    background: var(--app-background-color);
  }

  &.--checkbox:checked::before {
    background: var(--app-gradient-background-light-color);
    border: none;
  }

  &.--checkbox:checked::after {
    border-color: var(--app-text-color-secondary);
  }

  &.--radio {
    @extend %flex-column-center;

    appearance: none;
    border-radius: 100%;
    border: 1px solid var(--app-text-color-primary);
    opacity: 0.85;
  }

  &.--radio:checked {
    opacity: 1;
    border: none;
    background: var(--app-gradient-background-light-color);

    &::after {
      content: '';
      /* stylelint-disable-next-line value-keyword-case */
      width: v-bind(indicatorWidthPx);
      /* stylelint-disable-next-line value-keyword-case */
      height: v-bind(indicatorHeightPx);
      position: relative;
      background: var(--app-color-sendcloud-white);
      box-sizing: border-box;
      border-radius: 50%;
    }
  }

  &:disabled {
    background-color: var(--app-input-disabled);
    cursor: not-allowed;
  }
}
</style>
